import type { CatalogItem, CatalogSection, ProgressEntry } from '@lumos/contracts';
import { watchCompletedByPosition } from '@lumos/contracts';

/**
 * Сколько тайтл стоит наверху, прежде чем смениться следующим: 24 с — успеть
 * прочитать описание и не ловить смену посреди фразы (владелец, втрое дольше
 * прежних 8 с).
 */
export const HERO_ROTATE_MS = 24_000;

/** Больше точек под верхом не читается — это уже не подсказка, а шкала. */
const POOL_LIMIT = 10;

/**
 * Секции, из которых верх берёт тайтлы для перелистывания (владелец): только
 * эти три ряда и полка «Продолжить просмотр». Других рядов в наборе не бывает —
 * даже если нужных секций в ответе нет: тогда набор пуст (или из одной полки),
 * а наверху стоит витрина каталога.
 */
const POOL_SECTION_IDS = ['trending', 'popular-movies', 'popular-tv'];
const POOL_SECTION_TITLES = ['Сейчас смотрят', 'Популярные фильмы', 'Популярные сериалы'];

/**
 * Досмотренные тайтлы — их верх не предлагает.
 *
 * Судим по истории, которую главная и так читает. Фильм досмотрен, если запись
 * закрыта (`completed`) или позиция дошла до конца по тому же правилу, что у
 * сервера.
 *
 * У сериала запись — про серию, и досмотренная серия ещё не значит досмотренный
 * сериал: дальше может быть следующая. Есть ли она, уже решил сервер, собирая
 * полку «Продолжить просмотр»: сериал с досмотренной серией остаётся на полке,
 * пока есть непросмотренная следующая, и уходит с неё, когда смотреть больше
 * нечего. Поэтому сериал досмотрен, если его САМАЯ СВЕЖАЯ запись досмотрена, а
 * на полке его нет. `onShelf` — что сейчас на полке; `null` — полка ещё не
 * приехала (или не прочиталась), и о сериалах судить не по чему: не отсеиваем.
 */
export function finishedTitles(
  entries: readonly ProgressEntry[],
  onShelf: ReadonlySet<string> | null = null,
): Set<string> {
  const done = new Set<string>();
  /** Сериалы, у которых самая свежая запись уже встречена. */
  const seenSeries = new Set<string>();
  // История отсортирована от свежего к старому: первая запись сериала — та,
  // по которой сервер считает полку.
  for (const entry of entries) {
    const finished = entry.completed || watchCompletedByPosition(entry.positionMs, entry.durationMs);
    if (entry.season === null && entry.episode === null) {
      if (finished) done.add(entry.contentId);
      continue;
    }
    if (seenSeries.has(entry.contentId)) continue;
    seenSeries.add(entry.contentId);
    if (finished && onShelf !== null && !onShelf.has(entry.contentId)) done.add(entry.contentId);
  }
  return done;
}

/**
 * Сколько зритель должен ничего не делать на главной (не водить курсором, не
 * переводить фокус, не листать, не касаться), чтобы верх снова сам
 * перелистывал тайтлы (владелец: ~10 с).
 */
export const HERO_IDLE_MS = 10_000;

/**
 * Генератор случайных чисел с состоянием в числе (mulberry32).
 *
 * Своё «случайное», а не `Math.random`: порядок верха — чистая функция от
 * сохранённого зерна, поэтому повторная отрисовка (и двойная в StrictMode)
 * даёт тот же порядок, а тесты задают его зерном.
 */
function nextRandom(seed: number): { value: number; seed: number } {
  const state = (seed + 0x6d2b79f5) >>> 0;
  let t = state;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return { value: ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296, seed: state };
}

/** Перемешивание Фишера — Йетса; возвращает новый порядок и новое зерно. */
function shuffle<T>(items: readonly T[], seed: number): { items: T[]; seed: number } {
  const result = [...items];
  let state = seed;
  for (let index = result.length - 1; index > 0; index -= 1) {
    const step = nextRandom(state);
    state = step.seed;
    const other = Math.floor(step.value * (index + 1));
    [result[index], result[other]] = [result[other]!, result[index]!];
  }
  return { items: result, seed: state };
}

/** Зерно для порядка верха — одно на заход на главную. */
export function randomSeed(): number {
  return Math.floor(Math.random() * 4_294_967_296) >>> 0;
}

/**
 * Тайтлы, которые верх показывает сам, пока зритель ничего не трогает: из
 * «Сейчас смотрят», «Популярных фильмов», «Популярных сериалов» и полки
 * «Продолжить просмотр» вместе — и ни из каких других рядов (владелец). Без
 * повторов, без досмотренного и без телепередач: ток-шоу, реалити и новости
 * витрина держит среди сериалов, а наверху им не место. Передача это или
 * сериал, решает сервер (`CatalogItem.tvShow`) — одинаково для веба и
 * приложения. Не идут в набор и тайтлы без описания на русском
 * (`hasRussianOverview`, решение владельца 06.10.2026): наверху описание —
 * половина блока, и тайтл без него выглядит недоделанным. Сами ряды при этом
 * не меняются, и карточка в фокусе по-прежнему встаёт наверх, какой бы она
 * ни была: правило только про показ без участия зрителя.
 *
 * Точек под верхом больше десяти не читается, поэтому тайтлов в наборе не
 * больше `POOL_LIMIT`: каждый источник перемешивается по зерну и источники
 * берутся через один — так в наборе есть и начатое, и новое, а на каждом
 * заходе на главную набор свой. Зерно одно на заход: перечитанная полка
 * не перетасовывает уже показанное.
 */
/**
 * Есть ли у тайтла описание на русском.
 *
 * Витрина отдаёт описание на языке запроса, а без перевода — пустую строку;
 * изредка «переводом» записан английский текст. Поэтому мало непустой строки:
 * в описании должна быть кириллица. То же правило — в приложении
 * (`hasRussianOverview` в `HeroController.kt`) и у сервера, когда он выбирает
 * первый тайтл верха.
 */
export function hasRussianOverview(item: Pick<CatalogItem, 'overview'>): boolean {
  return /[а-яё]/i.test(item.overview ?? '');
}

export function heroPool(
  sections: readonly CatalogSection[],
  continueItems: readonly CatalogItem[],
  finished: ReadonlySet<string>,
  seed = 0,
): CatalogItem[] {
  const sources: (readonly CatalogItem[])[] = [
    continueItems,
    ...sections
      .filter((section) => section.layout !== 'hero' && section.items.length > 0)
      .filter((section) => POOL_SECTION_IDS.includes(section.id) || POOL_SECTION_TITLES.includes(section.title))
      .map((section) => section.items),
  ];
  // У каждого источника своё зерно: новая карточка на полке не меняет
  // порядок остальных источников.
  const lanes = sources.map((items, lane) =>
    shuffle(
      items.filter((item) => !item.tvShow && hasRussianOverview(item) && !finished.has(item.id)),
      (seed ^ Math.imul(lane + 1, 0x9e3779b1)) >>> 0,
    ).items,
  );
  const seen = new Set<string>();
  const pool: CatalogItem[] = [];
  const longest = Math.max(0, ...lanes.map((lane) => lane.length));
  for (let index = 0; index < longest && pool.length < POOL_LIMIT; index += 1) {
    for (const lane of lanes) {
      const item = lane[index];
      if (item === undefined || seen.has(item.id)) continue;
      seen.add(item.id);
      pool.push(item);
      if (pool.length === POOL_LIMIT) break;
    }
  }
  return pool;
}

/**
 * Порядок перелистывания: круг — перемешанный набор, по которому верх идёт
 * без повторов; кончился круг — новое перемешивание. `index` — какой тайтл
 * круга сейчас наверху (-1 — круг ещё не начат).
 */
export interface HeroRotation {
  readonly round: readonly string[];
  readonly index: number;
  readonly seed: number;
}

export function emptyRotation(seed: number): HeroRotation {
  return { round: [], index: -1, seed };
}

/** Тайтл, который круг ставит наверх сейчас; `null` — если такого в наборе уже нет. */
export function rotationCurrent(rotation: HeroRotation, ids: readonly string[]): string | null {
  const id = rotation.round[rotation.index];
  return id !== undefined && ids.includes(id) ? id : null;
}

/**
 * Следующий тайтл после текущего: дальше по кругу, пропуская выбывшие из
 * набора и `avoid` (то, что сейчас на экране, — например, карточку в фокусе).
 * Круг кончился — новое перемешивание, и его первый тайтл не совпадает с
 * тем, что на экране: одно и то же два раза подряд не стоит.
 */
export function advanceRotation(rotation: HeroRotation, ids: readonly string[], avoid: string | null): HeroRotation {
  if (ids.length === 0) return rotation;
  for (let index = rotation.index + 1; index < rotation.round.length; index += 1) {
    const id = rotation.round[index]!;
    if (id !== avoid && ids.includes(id)) return { ...rotation, index };
  }
  const shuffled = shuffle(ids, rotation.seed);
  const round = shuffled.items;
  if (round.length > 1 && round[0] === avoid) {
    // Не тасуем заново: меняем первый с любым другим — порядок остаётся случайным.
    const step = nextRandom(shuffled.seed);
    const other = 1 + Math.floor(step.value * (round.length - 1));
    [round[0], round[other]] = [round[other]!, round[0]!];
    return { round, index: 0, seed: step.seed };
  }
  return { round, index: 0, seed: shuffled.seed };
}

/**
 * Круг, приведённый к текущему набору: если его тайтл выбыл (полку
 * перечитали, история отсеяла досмотренное) или круг ещё не начат —
 * следующий подходящий. Чистая функция: её можно звать при каждой отрисовке.
 */
export function settleRotation(rotation: HeroRotation, ids: readonly string[]): HeroRotation {
  if (ids.length === 0 || rotationCurrent(rotation, ids) !== null) return rotation;
  return advanceRotation(rotation, ids, null);
}
